import {
    ActionIcon,
    Button,
    Group,
    Select,
    TextInput,
} from '@mantine/core';
import {
    IconAlignCenter,
    IconAlignLeft,
    IconAlignRight,
    IconBold,
    IconItalic,
    IconStrikethrough,
} from '@tabler/icons-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';

import sharedMessages from 'Components/utils/sharedMessages';

import styles from './AnnouncementForm.module.scss';

export type AnnouncementFormValues = {
    title: string;
    content: string;
    publishedAt: string;
};

export type AnnouncementFormHandle = {
    submit: () => Promise<void>;
};

interface Props {
    onSubmit: (values: AnnouncementFormValues) => Promise<void>;
    isLoading: boolean;
    hideActions?: boolean;
    onDisabledChange?: (disabled: boolean) => void;
    initialValues?: Partial<AnnouncementFormValues>;
}

const getContentText = (html: string) =>
    html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const AnnouncementForm = forwardRef<AnnouncementFormHandle, Props>(({
    onSubmit,
    isLoading,
    hideActions = false,
    onDisabledChange,
    initialValues,
}, ref) => {
    const intl = useIntl();

    const [title, setTitle] = useState(initialValues?.title ?? '');
    const [content, setContent] = useState(initialValues?.content ?? '');
    const [publishedAt] = useState(initialValues?.publishedAt ?? new Date().toISOString());

    const editor = useEditor({
        extensions: [
            StarterKit,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            TextStyle,
            Color,
        ],
        content,
        onUpdate: ({ editor: currentEditor }) => {
            setContent(currentEditor.getHTML());
        },
    });

    const isDisabled = useMemo(
        () => title.trim().length < 3 || getContentText(content).length < 3,
        [content, title],
    );

    useEffect(() => {
        onDisabledChange?.(isDisabled);
    }, [isDisabled, onDisabledChange]);

    const handleSubmit = async () => {
        if (isDisabled) return;

        await onSubmit({
            title: title.trim(),
            content,
            publishedAt,
        });
    };

    useImperativeHandle(ref, () => ({
        submit: handleSubmit,
    }));

    const getHeadingValue = () => {
        if (editor.isActive('heading', { level: 1 })) return '1';
        if (editor.isActive('heading', { level: 2 })) return '2';
        if (editor.isActive('heading', { level: 3 })) return '3';
        return '0';
    };

    const headingValue = getHeadingValue();

    const setHeading = (value: string | null) => {
        if (!value) return;

        const level = parseInt(value, 10);
        if (level === 0) {
            editor.chain().focus().setParagraph().run();
            return;
        }

        editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 }).run();
    };

    return (
        <div className={styles.container}>
            <TextInput
                id="announcement-title"
                label={intl.formatMessage(messages.title)}
                placeholder={intl.formatMessage(messages.titlePlaceholder)}
                value={title}
                maxLength={140}
                onChange={(e) => setTitle(e.currentTarget.value)}
                required
                radius="md"
                size="md"
            />

            <div className={styles.editorWrapper}>
                <span className={styles.editorLabel}>
                    {intl.formatMessage(messages.content)}
                </span>

                <Group className={styles.toolbar} gap="xs">
                    <Select
                        className={styles.headingSelect}
                        value={headingValue}
                        data={[
                            { value: '0', label: intl.formatMessage(messages.paragraph) },
                            { value: '1', label: `${intl.formatMessage(messages.heading)} 1` },
                            { value: '2', label: `${intl.formatMessage(messages.heading)} 2` },
                            { value: '3', label: `${intl.formatMessage(messages.heading)} 3` },
                        ]}
                        onChange={setHeading}
                        radius="md"
                        size="sm"
                        comboboxProps={{ withinPortal: false }}
                    />
                    <ActionIcon
                        variant={editor.isActive('bold') ? 'filled' : 'subtle'}
                        color="gray"
                        size="lg"
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        aria-label={intl.formatMessage(messages.bold)}
                    >
                        <IconBold size={18} />
                    </ActionIcon>
                    <ActionIcon
                        variant={editor.isActive('italic') ? 'filled' : 'subtle'}
                        color="gray"
                        size="lg"
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        aria-label={intl.formatMessage(messages.italic)}
                    >
                        <IconItalic size={18} />
                    </ActionIcon>
                    <ActionIcon
                        variant={editor.isActive('strike') ? 'filled' : 'subtle'}
                        color="gray"
                        size="lg"
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        aria-label={intl.formatMessage(messages.strike)}
                    >
                        <IconStrikethrough size={18} />
                    </ActionIcon>
                    <ActionIcon
                        variant={editor.isActive({ textAlign: 'left' }) ? 'filled' : 'subtle'}
                        color="gray"
                        size="lg"
                        onClick={() => editor.chain().focus().setTextAlign('left').run()}
                        aria-label={intl.formatMessage(messages.alignLeft)}
                    >
                        <IconAlignLeft size={18} />
                    </ActionIcon>
                    <ActionIcon
                        variant={editor.isActive({ textAlign: 'center' }) ? 'filled' : 'subtle'}
                        color="gray"
                        size="lg"
                        onClick={() => editor.chain().focus().setTextAlign('center').run()}
                        aria-label={intl.formatMessage(messages.alignCenter)}
                    >
                        <IconAlignCenter size={18} />
                    </ActionIcon>
                    <ActionIcon
                        variant={editor.isActive({ textAlign: 'right' }) ? 'filled' : 'subtle'}
                        color="gray"
                        size="lg"
                        onClick={() => editor.chain().focus().setTextAlign('right').run()}
                        aria-label={intl.formatMessage(messages.alignRight)}
                    >
                        <IconAlignRight size={18} />
                    </ActionIcon>
                </Group>

                <EditorContent editor={editor} className={styles.editor} />
            </div>

            {!hideActions && (
                <div className={styles.actions}>
                    <Button onClick={handleSubmit} disabled={isDisabled || isLoading} radius="md">
                        {intl.formatMessage(sharedMessages.save)}
                    </Button>
                </div>
            )}
        </div>
    );
});

const messages = defineMessages({
    title: {
        id: 'announcements.form.title',
        defaultMessage: 'Tytuł',
    },
    titlePlaceholder: {
        id: 'announcements.form.titlePlaceholder',
        defaultMessage: 'Np. Spotkanie rady parafialnej',
    },
    content: {
        id: 'announcements.form.content',
        defaultMessage: 'Treść',
    },
    paragraph: {
        id: 'announcments.form.paragraph',
        defaultMessage: 'Paragraf',
    },
    heading: {
        id: 'announcments.form.heading',
        defaultMessage: 'Nagłówek',
    },
    bold: {
        id: 'announcments.form.bold',
        defaultMessage: 'Pogrubienie',
    },
    italic: {
        id: 'announcments.form.italic',
        defaultMessage: 'Kursywa',
    },
    strike: {
        id: 'announcments.form.strike',
        defaultMessage: 'Przekreślenie',
    },
    alignLeft: {
        id: 'announcments.form.alignLeft',
        defaultMessage: 'Wyrównaj do lewej',
    },
    alignCenter: {
        id: 'announcments.form.alignCenter',
        defaultMessage: 'Wyśrodkuj',
    },
    alignRight: {
        id: 'announcments.form.alignRight',
        defaultMessage: 'Wyrównaj do prawej',
    },
});

export default AnnouncementForm;
